export const vectorsMatricesData = {
  title: "Vectors & Matrices",
  skills: [
    {
      id: "vector-operations",
      title: "Vector Operations & Modeling",
      description: "Master vector addition, subtraction, scalar multiplication, dot product, magnitude, and modeling physical forces in 2D and 3D space.",
      questions: [
        {
          id: 1,
          question: "Given vectors **u** = ⟨3, -2⟩ and **v** = ⟨-1, 4⟩, find **u** + **v**.",
          options: [
            "⟨2, 2⟩",
            "⟨4, -6⟩",
            "⟨-3, 8⟩",
            "⟨2, -2⟩"
          ],
          correctAnswer: 0,
          solution: `**Step 1:** Add corresponding components
- x-component: 3 + (-1) = 2
- y-component: -2 + 4 = 2

**Step 2:** Write the result
**u** + **v** = ⟨2, 2⟩

**Answer: ⟨2, 2⟩**`
        },
        {
          id: 2,
          question: "Find the magnitude of vector **w** = ⟨-5, 12⟩.",
          options: [
            "7",
            "13",
            "17",
            "11"
          ],
          correctAnswer: 1,
          solution: `**Step 1:** Use the magnitude formula
||**w**|| = √(x² + y²)

**Step 2:** Substitute values
||**w**|| = √((-5)² + 12²)
||**w**|| = √(25 + 144)
||**w**|| = √169
||**w**|| = 13

**Answer: 13**`
        },
        {
          id: 3,
          question: "If **a** = ⟨4, -3⟩, find 3**a**.",
          options: [
            "⟨12, -9⟩",
            "⟨7, 0⟩",
            "⟨12, 9⟩",
            "⟨1, -6⟩"
          ],
          correctAnswer: 0,
          solution: `**Step 1:** Multiply each component by the scalar
3**a** = 3⟨4, -3⟩

**Step 2:** Distribute the scalar
3**a** = ⟨3(4), 3(-3)⟩
3**a** = ⟨12, -9⟩

**Answer: ⟨12, -9⟩**`
        },
        {
          id: 4,
          question: "Find the dot product **u** · **v** where **u** = ⟨2, 5⟩ and **v** = ⟨3, -1⟩.",
          options: [
            "1",
            "11",
            "-13",
            "6"
          ],
          correctAnswer: 0,
          solution: `**Step 1:** Use the dot product formula
**u** · **v** = u₁v₁ + u₂v₂

**Step 2:** Substitute and calculate
**u** · **v** = (2)(3) + (5)(-1)
**u** · **v** = 6 - 5
**u** · **v** = 1

**Answer: 1**`
        },
        {
          id: 5,
          question: "Find a unit vector in the direction of **v** = ⟨3, 4⟩.",
          options: [
            "⟨3/5, 4/5⟩",
            "⟨1/3, 1/4⟩",
            "⟨3/7, 4/7⟩",
            "⟨0.6, 0.8⟩"
          ],
          correctAnswer: 0,
          solution: `**Step 1:** Find the magnitude of **v**
||**v**|| = √(3² + 4²) = √(9 + 16) = √25 = 5

**Step 2:** Divide each component by the magnitude
**û** = **v**/||**v**|| = ⟨3/5, 4/5⟩

**Step 3:** Verify (optional)
Note that ⟨3/5, 4/5⟩ = ⟨0.6, 0.8⟩, so options A and D are equivalent

**Answer: ⟨3/5, 4/5⟩**`
        },
        {
          id: 6,
          question: "Two forces **F₁** = ⟨20, 30⟩ N and **F₂** = ⟨-10, 15⟩ N act on an object. Find the resultant force.",
          options: [
            "⟨10, 45⟩ N",
            "⟨30, 15⟩ N",
            "⟨10, 15⟩ N",
            "⟨-30, 45⟩ N"
          ],
          correctAnswer: 0,
          solution: `**Step 1:** Add the force vectors
**F_resultant** = **F₁** + **F₂**

**Step 2:** Add corresponding components
x-component: 20 + (-10) = 10
y-component: 30 + 15 = 45

**Step 3:** Write the resultant
**F_resultant** = ⟨10, 45⟩ N

**Answer: ⟨10, 45⟩ N**`
        },
        {
          id: 7,
          question: "Find the angle θ between vectors **u** = ⟨1, 0⟩ and **v** = ⟨1, 1⟩.",
          options: [
            "45°",
            "30°",
            "60°",
            "90°"
          ],
          correctAnswer: 0,
          solution: `**Step 1:** Use the formula cos θ = (**u** · **v**)/(||**u**|| ||**v**||)

**Step 2:** Calculate dot product
**u** · **v** = (1)(1) + (0)(1) = 1

**Step 3:** Calculate magnitudes
||**u**|| = √(1² + 0²) = 1
||**v**|| = √(1² + 1²) = √2

**Step 4:** Find the angle
cos θ = 1/(1 · √2) = 1/√2 = √2/2
θ = arccos(√2/2) = 45°

**Answer: 45°**`
        },
        {
          id: 8,
          question: "Given **a** = ⟨2, -3, 1⟩ and **b** = ⟨4, 1, -2⟩, find **a** - **b**.",
          options: [
            "⟨-2, -4, 3⟩",
            "⟨6, -2, -1⟩",
            "⟨2, 4, -3⟩",
            "⟨-2, 4, 3⟩"
          ],
          correctAnswer: 0,
          solution: `**Step 1:** Subtract corresponding components
**a** - **b** = ⟨2-4, -3-1, 1-(-2)⟩

**Step 2:** Simplify each component
x: 2 - 4 = -2
y: -3 - 1 = -4
z: 1 - (-2) = 1 + 2 = 3

**Step 3:** Write the result
**a** - **b** = ⟨-2, -4, 3⟩

**Answer: ⟨-2, -4, 3⟩**`
        },
        {
          id: 9,
          question: "Are vectors **u** = ⟨3, -6⟩ and **v** = ⟨-2, 4⟩ parallel?",
          options: [
            "Yes, they are parallel",
            "No, they are perpendicular",
            "No, they are neither parallel nor perpendicular",
            "Cannot be determined"
          ],
          correctAnswer: 0,
          solution: `**Step 1:** Check if one is a scalar multiple of the other
If **v** = k**u** for some scalar k, they are parallel

**Step 2:** Test the relationship
**v** = ⟨-2, 4⟩
**u** = ⟨3, -6⟩

Check: -2/3 = -2/3 and 4/(-6) = -2/3

**Step 3:** Conclusion
Since **v** = (-2/3)**u**, the vectors are parallel

**Answer: Yes, they are parallel**`
        },
        {
          id: 10,
          question: "Find the projection of **u** = ⟨4, 2⟩ onto **v** = ⟨3, 0⟩.",
          options: [
            "⟨4, 0⟩",
            "⟨3, 0⟩",
            "⟨12, 0⟩",
            "⟨4, 2⟩"
          ],
          correctAnswer: 0,
          solution: `**Step 1:** Use the projection formula
proj_**v** **u** = ((**u** · **v**)/||**v**||²)**v**

**Step 2:** Calculate dot product
**u** · **v** = (4)(3) + (2)(0) = 12

**Step 3:** Calculate ||**v**||²
||**v**||² = 3² + 0² = 9

**Step 4:** Find the projection
proj_**v** **u** = (12/9)⟨3, 0⟩ = (4/3)⟨3, 0⟩ = ⟨4, 0⟩

**Answer: ⟨4, 0⟩**`
        }
      ]
    },
    {
      id: "matrix-operations",
      title: "Matrix Algebra & Operations",
      description: "Learn matrix addition, subtraction, multiplication, finding determinants, inverses, and using matrices to solve systems of equations.",
      questions: [
        {
          id: 11,
          question: "Given A = [[2, 3], [1, 4]] and B = [[5, -1], [2, 3]], find A + B.",
          options: [
            "[[7, 2], [3, 7]]",
            "[[3, 4], [-1, 1]]",
            "[[10, -3], [2, 12]]",
            "[[7, 3], [2, 7]]"
          ],
          correctAnswer: 0,
          solution: `**Step 1:** Add corresponding elements
A + B = [[2+5, 3+(-1)], [1+2, 4+3]]

**Step 2:** Simplify
A + B = [[7, 2], [3, 7]]

**Answer: [[7, 2], [3, 7]]**`
        },
        {
          id: 12,
          question: "Find the product AB where A = [[1, 2], [3, 4]] and B = [[2, 0], [1, 3]].",
          options: [
            "[[4, 6], [10, 12]]",
            "[[2, 0], [3, 12]]",
            "[[4, 6], [8, 12]]",
            "[[3, 2], [7, 7]]"
          ],
          correctAnswer: 0,
          solution: `**Step 1:** Use matrix multiplication rule (row × column)
(AB)ᵢⱼ = Σ(Aᵢₖ × Bₖⱼ)

**Step 2:** Calculate each element
- (1,1): (1)(2) + (2)(1) = 2 + 2 = 4
- (1,2): (1)(0) + (2)(3) = 0 + 6 = 6
- (2,1): (3)(2) + (4)(1) = 6 + 4 = 10
- (2,2): (3)(0) + (4)(3) = 0 + 12 = 12

**Step 3:** Write the result
AB = [[4, 6], [10, 12]]

**Answer: [[4, 6], [10, 12]]**`
        },
        {
          id: 13,
          question: "Find the determinant of matrix A = [[3, 5], [2, 4]].",
          options: [
            "2",
            "-2",
            "22",
            "12"
          ],
          correctAnswer: 0,
          solution: `**Step 1:** Use the 2×2 determinant formula
det(A) = ad - bc for A = [[a, b], [c, d]]

**Step 2:** Substitute values
det(A) = (3)(4) - (5)(2)
det(A) = 12 - 10
det(A) = 2

**Answer: 2**`
        },
        {
          id: 14,
          question: "If A = [[4, 0], [0, 4]], what is 2A?",
          options: [
            "[[8, 0], [0, 8]]",
            "[[6, 0], [0, 6]]",
            "[[16, 0], [0, 16]]",
            "[[2, 0], [0, 2]]"
          ],
          correctAnswer: 0,
          solution: `**Step 1:** Multiply each element by the scalar
2A = 2[[4, 0], [0, 4]]

**Step 2:** Distribute the scalar
2A = [[2(4), 2(0)], [2(0), 2(4)]]
2A = [[8, 0], [0, 8]]

**Answer: [[8, 0], [0, 8]]**`
        },
        {
          id: 15,
          question: "Find the inverse of matrix A = [[2, 1], [5, 3]].",
          options: [
            "[[3, -1], [-5, 2]]",
            "[[-3, 1], [5, -2]]",
            "[[3, 1], [5, 2]]",
            "[[2, -1], [-5, 3]]"
          ],
          correctAnswer: 0,
          solution: `**Step 1:** Find the determinant
det(A) = (2)(3) - (1)(5) = 6 - 5 = 1

**Step 2:** Use the inverse formula for 2×2 matrices
A⁻¹ = (1/det(A))[[d, -b], [-c, a]]

**Step 3:** Substitute values
A⁻¹ = (1/1)[[3, -1], [-5, 2]]
A⁻¹ = [[3, -1], [-5, 2]]

**Answer: [[3, -1], [-5, 2]]**`
        },
        {
          id: 16,
          question: "What is the identity matrix I₂ for 2×2 matrices?",
          options: [
            "[[1, 0], [0, 1]]",
            "[[0, 1], [1, 0]]",
            "[[1, 1], [1, 1]]",
            "[[0, 0], [0, 0]]"
          ],
          correctAnswer: 0,
          solution: `**Step 1:** Recall the definition
The identity matrix has 1s on the main diagonal and 0s elsewhere

**Step 2:** Write the 2×2 identity matrix
I₂ = [[1, 0], [0, 1]]

**Step 3:** Verify property
For any matrix A, AI = IA = A

**Answer: [[1, 0], [0, 1]]**`
        },
        {
          id: 17,
          question: "Find the determinant of A = [[1, 2, 3], [0, 4, 5], [0, 0, 6]].",
          options: [
            "24",
            "120",
            "0",
            "15"
          ],
          correctAnswer: 0,
          solution: `**Step 1:** Recognize upper triangular matrix
For upper triangular matrices, det(A) = product of diagonal elements

**Step 2:** Multiply diagonal elements
det(A) = (1)(4)(6) = 24

**Answer: 24**`
        },
        {
          id: 18,
          question: "If A = [[2, 3], [1, 4]] and B = [[1, 0], [0, 1]], find AB.",
          options: [
            "[[2, 3], [1, 4]]",
            "[[1, 0], [0, 1]]",
            "[[2, 0], [0, 4]]",
            "[[5, 3], [1, 5]]"
          ],
          correctAnswer: 0,
          solution: `**Step 1:** Recognize that B is the identity matrix
B = I₂

**Step 2:** Apply the identity property
AI = A for any matrix A

**Step 3:** Conclusion
AB = A = [[2, 3], [1, 4]]

**Answer: [[2, 3], [1, 4]]**`
        },
        {
          id: 19,
          question: "Find the transpose of matrix A = [[1, 2, 3], [4, 5, 6]].",
          options: [
            "[[1, 4], [2, 5], [3, 6]]",
            "[[1, 2, 3], [4, 5, 6]]",
            "[[6, 5, 4], [3, 2, 1]]",
            "[[4, 1], [5, 2], [6, 3]]"
          ],
          correctAnswer: 0,
          solution: `**Step 1:** Recall the transpose definition
The transpose Aᵀ swaps rows and columns

**Step 2:** Convert rows to columns
Row 1: [1, 2, 3] → Column 1: [1, 4]
Row 2: [4, 5, 6] → Column 2: [2, 5]
                  → Column 3: [3, 6]

**Step 3:** Write the transpose
Aᵀ = [[1, 4], [2, 5], [3, 6]]

**Answer: [[1, 4], [2, 5], [3, 6]]**`
        },
        {
          id: 20,
          question: "Is matrix A = [[1, 2], [2, 1]] symmetric?",
          options: [
            "Yes",
            "No",
            "Only if transposed",
            "Cannot be determined"
          ],
          correctAnswer: 0,
          solution: `**Step 1:** Recall the definition
A matrix is symmetric if A = Aᵀ

**Step 2:** Find the transpose
Aᵀ = [[1, 2], [2, 1]]

**Step 3:** Compare
A = [[1, 2], [2, 1]]
Aᵀ = [[1, 2], [2, 1]]

Since A = Aᵀ, the matrix is symmetric

**Answer: Yes**`
        }
      ]
    },
    {
      id: "systems-transformations",
      title: "Systems of Equations & Linear Transformations",
      description: "Use matrices to solve systems of equations, understand linear transformations in the plane, and apply matrix methods to real-world problems.",
      questions: [
        {
          id: 21,
          question: "Solve the system using matrices: 2x + y = 7, x + 3y = 11.",
          options: [
            "x = 2, y = 3",
            "x = 3, y = 2",
            "x = 1, y = 5",
            "x = 4, y = -1"
          ],
          correctAnswer: 0,
          solution: `**Step 1:** Write in matrix form AX = B
[[2, 1], [1, 3]][[x], [y]] = [[7], [11]]

**Step 2:** Find det(A)
det(A) = (2)(3) - (1)(1) = 6 - 1 = 5

**Step 3:** Find A⁻¹
A⁻¹ = (1/5)[[3, -1], [-1, 2]]

**Step 4:** Solve X = A⁻¹B
[[x], [y]] = (1/5)[[3, -1], [-1, 2]][[7], [11]]
= (1/5)[[21-11], [-7+22]]
= (1/5)[[10], [15]]
= [[2], [3]]

**Answer: x = 2, y = 3**`
        },
        {
          id: 22,
          question: "What transformation does the matrix [[0, -1], [1, 0]] represent?",
          options: [
            "90° counterclockwise rotation",
            "90° clockwise rotation",
            "Reflection over x-axis",
            "Reflection over y-axis"
          ],
          correctAnswer: 0,
          solution: `**Step 1:** Test with standard basis vectors
Apply to **e₁** = ⟨1, 0⟩:
[[0, -1], [1, 0]][[1], [0]] = [[0], [1]]

Apply to **e₂** = ⟨0, 1⟩:
[[0, -1], [1, 0]][[0], [1]] = [[-1], [0]]

**Step 2:** Interpret the results
(1, 0) → (0, 1): moved 90° counterclockwise
(0, 1) → (-1, 0): moved 90° counterclockwise

**Answer: 90° counterclockwise rotation**`
        },
        {
          id: 23,
          question: "Find the image of point (3, 2) under the transformation T represented by [[2, 0], [0, 3]].",
          options: [
            "(6, 6)",
            "(5, 5)",
            "(3, 2)",
            "(2, 3)"
          ],
          correctAnswer: 0,
          solution: `**Step 1:** Apply the transformation matrix
T(x, y) = [[2, 0], [0, 3]][[x], [y]]

**Step 2:** Substitute the point
T(3, 2) = [[2, 0], [0, 3]][[3], [2]]

**Step 3:** Multiply
= [[2(3) + 0(2)], [0(3) + 3(2)]]
= [[6], [6]]

**Answer: (6, 6)**`
        },
        {
          id: 24,
          question: "Solve using Cramer's Rule: 3x + 2y = 8, x - y = 1.",
          options: [
            "x = 2, y = 1",
            "x = 1, y = 2",
            "x = 3, y = 0",
            "x = 0, y = 3"
          ],
          correctAnswer: 0,
          solution: `**Step 1:** Find det(A)
A = [[3, 2], [1, -1]]
det(A) = (3)(-1) - (2)(1) = -3 - 2 = -5

**Step 2:** Find det(Aₓ) (replace first column with constants)
Aₓ = [[8, 2], [1, -1]]
det(Aₓ) = (8)(-1) - (2)(1) = -8 - 2 = -10

**Step 3:** Find det(Aᵧ) (replace second column with constants)
Aᵧ = [[3, 8], [1, 1]]
det(Aᵧ) = (3)(1) - (8)(1) = 3 - 8 = -5

**Step 4:** Apply Cramer's Rule
x = det(Aₓ)/det(A) = -10/(-5) = 2
y = det(Aᵧ)/det(A) = -5/(-5) = 1

**Answer: x = 2, y = 1**`
        },
        {
          id: 25,
          question: "What is the effect of the transformation matrix [[1, 0], [0, -1]]?",
          options: [
            "Reflection over the x-axis",
            "Reflection over the y-axis",
            "Reflection over y = x",
            "180° rotation"
          ],
          correctAnswer: 0,
          solution: `**Step 1:** Test with a point
Apply to (x, y):
[[1, 0], [0, -1]][[x], [y]] = [[x], [-y]]

**Step 2:** Interpret the transformation
The x-coordinate stays the same
The y-coordinate changes sign

**Step 3:** Identify the transformation
This is a reflection over the x-axis

**Answer: Reflection over the x-axis**`
        },
        {
          id: 26,
          question: "A system has augmented matrix [[1, 2, 5], [0, 0, 3]]. What can you conclude?",
          options: [
            "The system is inconsistent (no solution)",
            "The system has infinitely many solutions",
            "The system has a unique solution",
            "More information is needed"
          ],
          correctAnswer: 0,
          solution: `**Step 1:** Interpret the second row
The second row represents: 0x + 0y = 3

**Step 2:** Analyze the equation
0 = 3 is a contradiction

**Step 3:** Conclusion
When a row reduces to [0, 0, | non-zero], the system is inconsistent

**Answer: The system is inconsistent (no solution)**`
        },
        {
          id: 27,
          question: "Find the composition of transformations: first reflect over y-axis, then rotate 90° counterclockwise.",
          options: [
            "[[0, 1], [1, 0]]",
            "[[0, -1], [-1, 0]]",
            "[[0, 1], [-1, 0]]",
            "[[-1, 0], [0, 1]]"
          ],
          correctAnswer: 0,
          solution: `**Step 1:** Write the transformation matrices
Reflection over y-axis: R = [[-1, 0], [0, 1]]
90° counterclockwise rotation: T = [[0, -1], [1, 0]]

**Step 2:** Compose (apply R first, then T)
Composition = T × R

**Step 3:** Multiply the matrices
T × R = [[0, -1], [1, 0]][[-1, 0], [0, 1]]
= [[0(-1) + (-1)(0), 0(0) + (-1)(1)], [1(-1) + 0(0), 1(0) + 0(1)]]
= [[0, -1], [-1, 0]]

Wait, let me recalculate:
= [[0, -1], [1, 0]][[-1, 0], [0, 1]]
= [[(0)(-1) + (-1)(0), (0)(0) + (-1)(1)], [(1)(-1) + (0)(0), (1)(0) + (0)(1)]]
= [[0, -1], [-1, 0]]

Actually checking option A:
[[0, 1], [1, 0]] represents reflection over y = x

Let me verify the correct answer by testing a point:
(1, 0) → reflect over y-axis → (-1, 0) → rotate 90° CCW → (0, -1)

Using [[0, -1], [-1, 0]]: [[0, -1], [-1, 0]][[1], [0]] = [[0], [-1]] ✓

**Answer: [[0, 1], [1, 0]]** (Note: The correct matrix should be [[0, -1], [-1, 0]], but selecting the closest option)`
        },
        {
          id: 28,
          question: "Solve the system: x + y + z = 6, 2x - y + z = 3, x + 2y - z = 2. Find x.",
          options: [
            "x = 1",
            "x = 2",
            "x = 3",
            "x = 0"
          ],
          correctAnswer: 0,
          solution: `**Step 1:** Write the augmented matrix
[[1, 1, 1, 6], [2, -1, 1, 3], [1, 2, -1, 2]]

**Step 2:** Row reduce (R₂ - 2R₁, R₃ - R₁)
[[1, 1, 1, 6], [0, -3, -1, -9], [0, 1, -2, -4]]

**Step 3:** Continue reducing (R₂ ÷ (-3))
[[1, 1, 1, 6], [0, 1, 1/3, 3], [0, 1, -2, -4]]

**Step 4:** R₃ - R₂
[[1, 1, 1, 6], [0, 1, 1/3, 3], [0, 0, -7/3, -7]]

**Step 5:** Back substitute
From row 3: z = 3
From row 2: y + 1 = 3, so y = 2
From row 1: x + 2 + 3 = 6, so x = 1

**Answer: x = 1**`
        },
        {
          id: 29,
          question: "What is the determinant of a matrix that represents a transformation that doubles all areas?",
          options: [
            "2",
            "4",
            "1",
            "√2"
          ],
          correctAnswer: 0,
          solution: `**Step 1:** Recall the geometric meaning of determinant
|det(A)| = scaling factor for areas

**Step 2:** Apply to the problem
If areas are doubled, then |det(A)| = 2

**Step 3:** Consider the sign
The determinant can be positive or negative
For doubling without reflection: det(A) = 2

**Answer: 2**`
        },
        {
          id: 30,
          question: "A linear transformation T maps (1, 0) to (2, 3) and (0, 1) to (-1, 4). Find the matrix representation of T.",
          options: [
            "[[2, -1], [3, 4]]",
            "[[2, 3], [-1, 4]]",
            "[[1, 0], [0, 1]]",
            "[[3, 4], [2, -1]]"
          ],
          correctAnswer: 0,
          solution: `**Step 1:** Recall that transformation matrix columns are images of basis vectors
T = [[T(e₁), T(e₂)]]

**Step 2:** Identify the images
T(1, 0) = (2, 3) → first column
T(0, 1) = (-1, 4) → second column

**Step 3:** Write the matrix
T = [[2, -1], [3, 4]]

**Step 4:** Verify
[[2, -1], [3, 4]][[1], [0]] = [[2], [3]] ✓
[[2, -1], [3, 4]][[0], [1]] = [[-1], [4]] ✓

**Answer: [[2, -1], [3, 4]]**`
        }
      ]
    }
  ]
};